import { inject, injectable } from "inversify";
import { DependencyType } from "@di/DependencyType";
import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import {
  CreateSerialRangeDocument,
  CreateSerialRangeMutation,
  CreateSerialRangeMutationVariables,
  SerialRangesDocument,
  SerialRangesQuery,
  SerialRangesQueryVariables,
  CreateSerialRangeInput,
  UpdateSerialRangeDocument,
  UpdateSerialRangeInput,
  UpdateSerialRangeMutation,
  UpdateSerialRangeMutationVariables,
} from "generated/graphql";
import { parseServerError } from "@util/parseError";

type SerialRangeNode = NonNullable<
  NonNullable<SerialRangesQuery["serialRanges"]>["nodes"]
>[number];

@injectable()
export class SerialRangeService {
  constructor(
    @inject(DependencyType.ApolloClient)
    private readonly apolloClient: ApolloClient<NormalizedCacheObject>
  ) {}

  async getAllSerialRanges(refresh = false) {
    const fetchPolicy = refresh ? "network-only" : ("cache-first" as const);
    const all: SerialRangeNode[] = [];
    let after: string | undefined;

    while (true) {
      const result = await this.apolloClient.query<
        SerialRangesQuery,
        SerialRangesQueryVariables
      >({
        query: SerialRangesDocument,
        variables: { after },
        fetchPolicy,
      });

      if (result.errors || !result.data?.serialRanges) {
        throw new Error("Failed to get serial ranges");
      }

      const connection = result.data.serialRanges;
      all.push(...(connection.nodes ?? []));
      if (!connection.pageInfo?.hasNextPage || !connection.pageInfo.endCursor) {
        break;
      }
      after = connection.pageInfo.endCursor;
    }

    // Sort by productType.code (string) THEN start number
    const sorted = [...all];
    sorted.sort((a, b) => {
      const aCode = a.productType?.code ?? "";
      const bCode = b.productType?.code ?? "";
      if (aCode > bCode) {
        return 1;
      } else if (aCode < bCode) {
        return -1;
      } else {
        return a.startNumber - b.startNumber;
      }
    });
    return sorted;
  }

  async createSerialRange(input: CreateSerialRangeInput) {
    const result = await this.apolloClient.mutate<
      CreateSerialRangeMutation,
      CreateSerialRangeMutationVariables
    >({
      mutation: CreateSerialRangeDocument,
      variables: {
        input,
      },
    });

    if (result.errors || !result.data?.createSerialRange) {
      return parseServerError(result.errors?.[0]);
    }

    return result.data.createSerialRange.id;
  }

  async updateSerialRange(input: UpdateSerialRangeInput) {
    const result = await this.apolloClient.mutate<
      UpdateSerialRangeMutation,
      UpdateSerialRangeMutationVariables
    >({
      mutation: UpdateSerialRangeDocument,
      variables: {
        input,
      },
    });

    if (result.errors || !result.data?.updateSerialRange) {
      return parseServerError(result.errors?.[0]);
    }

    return result.data.updateSerialRange.id;
  }
}
